import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';
import { watchPortalRequests } from '../../data/familyPortal';
import { familyPortalWord as w } from '../../domain/familyPortal';
import { formatCredits } from '../../domain/makeup';
import { normaliseTimeZone, wallClockIn, zoneAbbreviation } from '../../domain/timeZone';
import s from './FamilyPortalActivity.module.css';

// The teacher's "Family Portal activity" (#773, #543 §5): every request a
// family made in the Family Portal, newest first — each booking (#773), each
// cancellation (#775) and each reschedule (#543). Read from
// `users/{uid}/portalRequests`, which only the booking, cancel and reschedule
// callables write (functions/portal/book.js, cancel.js, reschedule.js), so it is
// what the server did. A booking the family later cancelled reads "Booked, then
// cancelled", and the cancel is its own row saying what the studio's policy
// did. A reschedule is one row on the new time, saying where it moved from,
// what the policy did to that lesson, and when the credit did not cover it.
//
// On Business Settings → Family Portal under its Visibility settings (#774,
// SettingsPage.jsx). MMS has no list like it: its teacher learns of a booking
// from the email and an in-app notice (#543 §2). The booked lesson is on the
// calendar, the web's and both phones', as an ordinary attendee on the
// occurrence.

const millis = (v) => {
  if (v == null) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// What became of a reschedule's shortfall (functions/portal/settle.js), or
// null while it is still owing or never was.
function settlementOf(r, fmt) {
  const done = r.settlement;
  if (!done) return null;
  if (done.status === 'charged') {
    return Number.isFinite(done.amountMinor)
      ? `${w('activityShortCharged')} ${fmt.formatMoney(done.amountMinor)}`
      : w('activityShortCharged');
  }
  if (done.status === 'covered') return w('activityShortCovered');
  if (done.status === 'unpriced') return w('activityShortUnpriced');
  return null;
}

// What a record's Status column says. `fmt` writes a reschedule's
// moved-from date.
function statusOf(r, fmt) {
  if (r.kind === 'reschedule') {
    const from = r.from || {};
    const did = from.attendance === 'remove' ? w('activityRemoved') : (from.attendanceLabel || '');
    return [
      r.status === 'cancelled' ? w('activityRescheduledThenCancelled') : w('activityRescheduled'),
      from.date ? `${w('activityMovedFrom')} ${fmt.formatDate(from.date)}${from.startTime ? ` ${fmt.formatTime(from.startTime)}` : ''}` : null,
      did || null,
      r.credit && r.credit.covered === false ? w('activityShort') : null,
      settlementOf(r, fmt),
    ].filter(Boolean).join(' · ');
  }
  if (r.kind === 'cancel') {
    const did = r.attendance === 'remove' ? w('activityRemoved') : (r.attendanceLabel || '');
    return [w('activityCancelled'), did].filter(Boolean).join(' · ');
  }
  if (r.status === 'booked') return w('activityBooked');
  if (r.status === 'cancelled') return w('activityBookedThenCancelled');
  return r.status || '';
}

/**
 * One row per record, as the list prints it: pure, so the words are pinned
 * without Firestore. `fmt` is the studio's formatters (`useLocale()`),
 * `timeZone` the studio's, for when the booking was made.
 */
export function activityRows(records = [], { fmt, timeZone = null }) {
  const zone = normaliseTimeZone(timeZone) || null;
  return records.map((r) => {
    const at = millis(r.createdAt);
    const made = at == null ? null : wallClockIn(at, zone || 'UTC');
    const label = r.startTime && r.timeZone
      ? zoneAbbreviation(r.timeZone, r.date, r.startTime, fmt.locale || 'en-US')
      : '';
    const lessonWhen = [
      r.date ? fmt.formatDate(r.date) : null,
      r.startTime ? `${fmt.formatTime(r.startTime)}${label ? ` ${label}` : ''}` : null,
    ].filter(Boolean).join(' ');
    return {
      id: r.id,
      when: made ? `${fmt.formatDate(made.date)} ${fmt.formatTime(made.time)}` : '',
      by: [r.names?.bookedBy || r.names?.cancelledBy || r.names?.rescheduledBy, r.names?.family].filter(Boolean).join(' · '),
      student: r.names?.student || '',
      lesson: [lessonWhen, r.categoryLabel].filter(Boolean).join(' · '),
      credit: r.kind !== 'cancel' && r.credit ? formatCredits(r.credit.credits, fmt.decimalSeparator) : '—',
      status: statusOf(r, fmt),
    };
  });
}

export default function FamilyPortalActivity({ timeZone = null }) {
  const { user } = useAuth();
  const fmt = useLocale();
  const uid = user?.uid ?? null;
  const [state, setState] = useState({ uid: null, rows: null, failed: false });

  useEffect(() => {
    if (!uid) return undefined;
    return watchPortalRequests(
      uid,
      (rows) => setState({ uid, rows, failed: false }),
      () => setState({ uid, rows: null, failed: true }),
    );
  }, [uid]);

  const mine = state.uid === uid ? state : { rows: null, failed: false };
  const rows = mine.rows ? activityRows(mine.rows, { fmt, timeZone }) : null;

  return (
    <section className={s.panel} aria-labelledby="family-portal-activity">
      <h2 id="family-portal-activity" className={s.title}>{w('activityTitle')}</h2>
      <p className={s.lead}>{w('activityLead')}</p>
      {mine.failed ? <p className={s.empty}>{w('activityLoadFailed')}</p> : null}
      {!mine.failed && rows == null ? <p className={s.empty}>{w('loading')}</p> : null}
      {rows && rows.length === 0 ? <p className={s.empty}>{w('activityEmpty')}</p> : null}
      {rows && rows.length > 0 ? (
        <div className={s.scroll}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{w('activityWhen')}</th>
                <th>{w('activityBy')}</th>
                <th>{w('activityStudent')}</th>
                <th>{w('activityLesson')}</th>
                <th>{w('activityCredit')}</th>
                <th>{w('activityStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.when}</td>
                  <td>{r.by}</td>
                  <td>{r.student}</td>
                  <td>{r.lesson}</td>
                  <td>{r.credit}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
